import React, { useState, useEffect } from 'react';
import axiosClient from '../../api/axiosClient';
import { ShieldAlert, Download, Search, Server, Fingerprint } from 'lucide-react';

const ForensicLedgerPage = () => {
    const [groupedReports, setGroupedReports] = useState({});
    const [loading, setLoading] = useState(true);

    const fetchReports = async () => {
        setLoading(true);
        try {
            // This endpoint returns grouped data: { "AgentName": { details... , attacks: [] } }
            const data = await axiosClient.get('/admin/sentinel/hacker-intel');
            setGroupedReports(data || {});
        } catch (err) { 
            console.error("Forensic Ledger Sync Error:", err);
            // Silent error handling to prevent UI crash
        } finally { 
            setLoading(false); 
        }
    };

    useEffect(() => { fetchReports(); }, []);

    const downloadPDF = async (id, ip) => {
        console.log(`[SOVEREIGN-EXPORT] Forging Evidence PDF for ${ip}...`);
        try {
            const response = await axiosClient.get(`/admin/sentinel/hacker-intel/${id}/pdf`, {
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(new Blob([response]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `AIRS_Forensic_Report_${ip}.pdf`);
            document.body.appendChild(link);
            link.click();
            
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
            console.log(`[SOVEREIGN-EXPORT] Evidence Secured for IP: ${ip}`);
        } catch (err) {
            console.error("PDF Download Failed:", err);
            alert("Forensic Export Failed: Handshake rejected by Native AIRS Analysis Engine.");
        }
    };

    return (
        <div className="p-6 space-y-6 bg-slate-50 min-h-screen">
            {/* --- HEADER --- */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                        <ShieldAlert className="text-red-600" /> Fleet Intelligence Ledger
                    </h1>
                    <p className="text-sm text-slate-500 font-mono uppercase tracking-tight">Audit Domain: Sovereign-Grid / Forensics</p>
                </div>
                <button 
                    onClick={fetchReports} 
                    disabled={loading}
                    className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-all text-sm font-bold shadow-lg disabled:opacity-50"
                >
                    <Search size={16} className={loading ? "animate-spin" : ""} /> 
                    {loading ? "Decrypting..." : "Refresh Records"}
                </button>
            </div>

            {/* --- GROUPED FLEET LIST --- */}
            {Object.keys(groupedReports).length === 0 ? (
                <div className="text-center py-24 bg-white rounded-3xl border-2 border-dashed border-slate-200">
                    <Fingerprint size={48} className="mx-auto text-slate-200 mb-4" />
                    <p className="text-slate-400 font-medium italic">No adversarial data intercepted in the current sovereign cycle.</p>
                </div>
            ) : (
                Object.entries(groupedReports).map(([agentName, data]) => (
                    <div key={data.agent_id} className="mb-10 bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-200 overflow-hidden transition-all hover:border-slate-300">
                        
                        {/* 🛡️ AGENT IDENTITY STRIP (Millions-Scale Management) */}
                        <div className="bg-slate-950 p-5 flex justify-between items-center text-white">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-airs-500/10 rounded-2xl border border-airs-500/20">
                                    <Server size={24} className="text-airs-400" />
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h2 className="font-bold text-xl tracking-tight">{agentName}</h2>
                                        <span className="text-[10px] text-airs-400 font-black">
                                            PROTECTED BY SENTINEL: {data.agent_id} 
                                        </span>
                                        {data.agent_id === "00000000-0000-0000-0000-000000000000" && (
                                            <span className="bg-yellow-500/20 text-yellow-500 px-2 rounded text-[9px]">SYSTEM DEFAULT</span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-3 mt-1">
                                        <span className="text-[9px] bg-white/10 px-2 py-0.5 rounded text-slate-300 uppercase tracking-widest">{data.os}</span>
                                        <span className="text-[10px] text-slate-500 font-mono">BINDING_ID: {data.agent_id}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-red-500/10 border border-red-500/20 px-4 py-2 rounded-2xl text-center">
                                <div className="text-[9px] text-red-400 uppercase font-black tracking-widest mb-0.5">Captures</div>
                                <div className="text-lg font-black text-red-500 leading-none">{data.attacks ? data.attacks.length : 0}</div>
                            </div>
                        </div>

                        {/* NESTED FORENSIC TABLE */}
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead className="bg-slate-50 border-b border-slate-100">
                                    <tr className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                        <th className="px-8 py-4">Capture Timestamp</th>
                                        <th className="px-8 py-4">De-Anonymized IP</th>
                                        <th className="px-8 py-4">Methodology</th>
                                        <th className="px-8 py-4 text-right">Operation</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {data.attacks && data.attacks.map((attack) => (
                                        <tr key={attack.id} className="hover:bg-red-50/30 transition-colors group">
                                            <td className="px-8 py-5 text-xs text-slate-500 font-mono">
                                                {new Date(attack.captured_at).toLocaleString()}
                                            </td>
                                            <td className="px-8 py-5">
                                                <div className="flex items-center gap-3">
                                                    <span className="font-mono font-bold text-red-600 text-sm">{attack.real_ip}</span>
                                                    <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></div>
                                                </div>
                                            </td>
                                            <td className="px-8 py-5">
                                                <span className="text-[10px] bg-slate-800 text-slate-200 px-3 py-1 rounded-full font-bold uppercase tracking-tighter">
                                                    {attack.trace_method || "Neural Ambush"}
                                                </span>
                                            </td>
                                            <td className="px-8 py-5 text-right">
                                                <button 
                                                    onClick={() => downloadPDF(attack.id, attack.real_ip)}
                                                    className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-1.5 rounded-lg hover:bg-blue-700 transition-all font-bold text-[10px] uppercase tracking-widest shadow-md shadow-blue-200"
                                                >
                                                    <Download size={12} /> EXPORT EVIDENCE
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ))
            )}
        </div>
    );
};

export default ForensicLedgerPage;